import { forwardRef } from 'react';

import { Skeleton } from 'components/Skeleton';
import { Spinner } from 'components/Spinner';
import { cn } from 'components/utils';

import {
  menuStatusDescriptionRecipe,
  menuStatusRecipe,
  menuStatusSkeletonRowRecipe,
  menuStatusTitleRecipe,
} from './MenuStatus.recipe';

import type { MenuStatusProps } from './types';

const SKELETON_LINES = ['first', 'second', 'third'] as const;

/**
 * Design System 2.0 menu status: what a popup's list shows while its options load, or when none match.
 *
 * Pass it as the `status` of `Menu`, `Select` or `MultiSelect`; each draws it in place of the rows,
 * outside keyboard selection, so it cannot be mistaken for a choice.
 *
 * Three kinds. `loading` when the options are unknown, `skeleton` when the row shape is known, and
 * `empty` when the query ran and nothing matched. A load that ends in nothing becomes `empty`; one
 * that fails is the screen's error, not this component's.
 *
 * Announcements differ on purpose. `loading` is its own `role="status"` through `Spinner`, delayed so
 * a load that finishes first says nothing. `skeleton` is silent and the region names what is loading
 * with one visually hidden status. `empty` is a polite status, so a search that ends in nothing is
 * heard; the result count for a Search field belongs to its own `helperText`.
 *
 * Every loading state ends in content, an empty state or an error. After about ten seconds, say so
 * and offer Try again and Cancel; that is the screen's job, not this component's.
 */
export const MenuStatus = forwardRef<HTMLDivElement, MenuStatusProps>((props, ref) => {
  if (props.kind === 'loading') {
    const { kind, label, className, ...rest } = props;
    return (
      <div
        ref={ref}
        {...rest}
        // After the spread, and left out of the prop type: the Spinner inside is the status.
        aria-busy={undefined}
        className={cn(menuStatusRecipe({ kind }), className)}
        data-kind="loading"
        role={undefined}
      >
        <Spinner delayed showLabel label={label} size="xs" />
      </div>
    );
  }

  if (props.kind === 'skeleton') {
    const { kind, label, className, ...rest } = props;
    return (
      <div
        ref={ref}
        {...rest}
        // After the spread: busy, and silent itself. The hidden status inside names what is loading.
        aria-busy
        className={cn(menuStatusRecipe({ kind }), className)}
        data-kind="skeleton"
        role={undefined}
      >
        <span className="sr-only" role="status">
          {label}
        </span>
        {SKELETON_LINES.map((line) => (
          <div key={line} aria-hidden className={menuStatusSkeletonRowRecipe({ line })}>
            <Skeleton className="w-full" />
          </div>
        ))}
      </div>
    );
  }

  const { kind, title, description, className, ...rest } = props;
  return (
    <div
      ref={ref}
      {...rest}
      aria-busy={undefined}
      className={cn(menuStatusRecipe({ kind }), className)}
      data-kind="empty"
      role="status"
    >
      <p className={menuStatusTitleRecipe()}>{title}</p>
      {description && <p className={menuStatusDescriptionRecipe()}>{description}</p>}
    </div>
  );
});
